import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard, Map, GraduationCap, Award, Bot, FileCheck2, CalendarClock,
  BookOpen, MessageSquare, Settings, LogOut, Menu, Sparkles, LoaderCircle, Crown, Languages, Lightbulb, X, Inbox, Handshake, Store, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export const DASHBOARD_NAV = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-tableau-de-bord" },
  { to: "/dashboard/parcours", label: "Mon parcours", icon: Map, testid: "nav-mon-parcours" },
  { to: "/dashboard/coach", label: "Coach civique (IA)", icon: GraduationCap, testid: "nav-coach-civique" },
  { to: "/dashboard/examen", label: "Examen & Quiz", icon: Award, testid: "nav-examen-quiz" },
  { to: "/dashboard/assistant", label: "Assistant démarches (IA)", icon: Bot, testid: "nav-assistant-demarches" },
  { to: "/dashboard/interprete", label: "Interprète IA", icon: Languages, testid: "nav-interprete" },
  { to: "/dashboard/documents", label: "Mes documents", icon: FileCheck2, testid: "nav-mes-documents" },
  { to: "/dashboard/echeances", label: "Mes échéances", icon: CalendarClock, testid: "nav-mes-echeances" },
  { to: "/dashboard/ressources", label: "Formations & Ressources", icon: BookOpen, testid: "nav-formations" },
  { to: "/dashboard/partenaires", label: "Partenaires", icon: Handshake, testid: "nav-partenaires" },
  { to: "/dashboard/messages", label: "Messages", icon: MessageSquare, badge: true, testid: "nav-messages" },
  { to: "/dashboard/parametres", label: "Paramètres", icon: Settings, testid: "nav-parametres" },
];

function NavItem({ item, onClick, unread }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[13px] font-medium transition-all duration-200 ${
          isActive ? "bg-bleu text-white shadow-[0_8px_20px_-8px_rgba(30,63,174,0.6)]" : "text-slate-600 hover:bg-bleu-light hover:text-bleu"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge && unread > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rouge px-1.5 text-[10px] font-bold text-white" data-testid="nav-messages-badge">{unread}</span>
      )}
      {item.soon && (
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-400 group-hover:bg-white/60">
          Bientôt
        </span>
      )}
    </NavLink>
  );
}

function PlanCard({ user, onNavigate }) {
  const premium = user.plan === "premium";
  return premium ? (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="sidebar-plan-premium">
      <p className="flex items-center gap-2 text-sm font-bold text-amber-700">
        <Crown className="h-4 w-4" /> Premium actif
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-amber-700/80">Accès illimité à tous les outils INTEGRA FRANCE.</p>
    </div>
  ) : (
    <div className="rounded-2xl bg-bleu-night p-4 text-white" data-testid="sidebar-plan-free">
      <p className="flex items-center gap-2 text-sm font-bold"><Sparkles className="h-4 w-4 text-bleu-accent" /> Compte gratuit</p>
      <p className="mt-1 text-[11px] leading-relaxed text-white/60">Débloquez l'IA illimitée et les examens blancs.</p>
      <Link
        to="/dashboard/parametres"
        onClick={onNavigate}
        className="mt-3 block rounded-xl bg-white py-2 text-center text-xs font-bold text-bleu transition-colors hover:bg-bleu-light"
        data-testid="sidebar-upgrade-link"
      >
        Passer à Premium — 7,99 €/mois
      </Link>
    </div>
  );
}

function Sidebar({ user, onNavigate, unread }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-5 pt-6">
        <Link to="/" onClick={onNavigate}><Logo compact /></Link>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4" aria-label="Navigation espace personnel">
        {DASHBOARD_NAV.map((item) => <NavItem key={item.to} item={item} onClick={onNavigate} unread={unread} />)}
        {user.role === "partner" && (
          <NavItem
            item={{ to: "/dashboard/partenaire", label: "Espace partenaire", icon: Store, testid: "nav-espace-partenaire" }}
            onClick={onNavigate}
          />
        )}
      </nav>
      <div className="border-t border-slate-100 p-3">
        <PlanCard user={user} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

const SUGGESTION_CATEGORIES = ["Amélioration", "Nouvelle fonctionnalité", "Bug", "Contenu", "Autre"];

export const SUGGESTION_STATUS_META = {
  nouvelle: { label: "Nouvelle", cls: "bg-slate-100 text-slate-600" },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  traitee: { label: "Traitée", cls: "bg-emerald-100 text-emerald-700" },
  rejetee: { label: "Rejetée", cls: "bg-rose-100 text-rose-600" },
};

function SuggestionModal({ onClose }) {
  const [form, setForm] = useState({ category: SUGGESTION_CATEGORIES[0], message: "" });
  const [sending, setSending] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = () => api.get("/suggestions/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { loadMine(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/suggestions", form);
      toast.success("Merci ! Votre suggestion a bien été transmise à l'équipe INTEGRA FRANCE.");
      setForm((f) => ({ ...f, message: "" }));
      loadMine();
    } catch {
      toast.error("Impossible d'envoyer votre suggestion. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onClose} data-testid="suggestion-modal">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-slate-900">
            <Lightbulb className="h-5 w-5 text-ambre" /> Suggérer une amélioration
          </h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="suggestion-close"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">Votre avis compte : aidez-nous à améliorer et enrichir INTEGRA FRANCE.</p>
        <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="suggestion-category-select">
          {SUGGESTION_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={4} required minLength={5} maxLength={1000}
          placeholder="Décrivez votre idée, le contenu manquant ou le problème rencontré…"
          className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="suggestion-message-input" />
        <button type="submit" disabled={sending || form.message.trim().length < 5} className="btn-primary mt-4 w-full !py-3 disabled:opacity-50" data-testid="suggestion-submit">
          {sending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Envoyer ma suggestion"}
        </button>

        {mine.length > 0 && (
          <div className="mt-6 border-t border-slate-100 pt-4" data-testid="my-suggestions-list">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Mes suggestions</p>
            <div className="mt-2 max-h-56 space-y-2 overflow-y-auto pr-1">
              {mine.map((s) => {
                const st = SUGGESTION_STATUS_META[s.status] || SUGGESTION_STATUS_META.nouvelle;
                return (
                  <div key={s.id} className="rounded-xl border border-slate-100 p-3" data-testid="my-suggestion-item">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{s.category} · {new Date(s.created_at).toLocaleDateString("fr-FR")}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`} data-testid="my-suggestion-status">{st.label}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{s.message}</p>
                    {s.admin_response && (
                      <div className="mt-2 rounded-lg bg-bleu-light/70 p-2.5" data-testid="my-suggestion-response">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-bleu">Réponse de l'équipe</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-slate-700">{s.admin_response}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    window.addEventListener("notifications-updated", load);
    return () => window.removeEventListener("notifications-updated", load);
  }, [user]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="dashboard-loading">
        <LoaderCircle className="h-8 w-8 animate-spin text-bleu" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;
  if (["admin", "super_admin", "moderator"].includes(user.role)) return <Navigate to="/admin" replace />;

  const handleLogout = async () => {
    await logout();
    toast.success("Vous êtes déconnecté. À bientôt !");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[264px] border-r border-slate-100 bg-white lg:block">
        <Sidebar user={user} unread={unread} />
      </aside>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "tween", duration: 0.25 }}
              className="fixed inset-y-0 left-0 z-50 w-[264px] bg-white shadow-2xl lg:hidden"
              data-testid="dashboard-mobile-sidebar"
            >
              <Sidebar user={user} onNavigate={() => setOpen(false)} unread={unread} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-[264px]">
        <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/85 backdrop-blur-xl">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-8">
            <div className="flex items-center gap-3">
              <button
                className="rounded-xl border border-slate-200 p-2 text-slate-600 lg:hidden"
                onClick={() => setOpen(true)}
                aria-label="Ouvrir le menu"
                data-testid="dashboard-menu-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <p className="text-sm font-bold text-slate-900" data-testid="dashboard-greeting">Bonjour, {user.name?.split(" ")[0]} 👋</p>
                <p className="hidden text-[11px] text-slate-400 sm:block">
                  {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setSuggestOpen(true)}
                className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                data-testid="suggestion-open-button"
              >
                <Lightbulb className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Suggérer</span>
              </button>
              {user.plan === "premium" && (
                <span className="hidden items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-[11px] font-bold text-amber-700 sm:flex" data-testid="topbar-premium-badge">
                  <Crown className="h-3 w-3" /> Premium
                </span>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-rouge/40 hover:text-rouge"
                data-testid="dashboard-logout-button"
              >
                <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </header>
        <main className="px-4 py-7 sm:px-8">
          <Outlet />
        </main>
      </div>
      {suggestOpen && <SuggestionModal onClose={() => setSuggestOpen(false)} />}
    </div>
  );
}
